import { api } from '../api';

/**
 * As chamadas do campo — equipes, veículos e pontos de investigação.
 *
 * Cada função carrega o caminho INTEIRO, escrito. É mais verboso e é o
 * ponto: o caminho é o contrato entre a tela e a API, e o teste 26 lê os
 * `@Post` dos controladores e procura por ele aqui. Caminho montado a
 * partir de sufixo existe só em tempo de execução — e o que não se acha
 * procurando não se confere.
 */

export interface Equipe {
  id: string;
  codigo: string;
  nome: string;
  tipo: string;
  base: string | null;
  situacao: string;
  observacoes: string | null;
  responsavel_id: string | null;
  responsavel_nome: string | null;
  membros_hoje: number;
}

export interface EquipeMembro {
  id: string;
  equipe_id: string;
  funcionario_id: string;
  funcionario_nome: string;
  funcionario_cargo: string | null;
  papel: string;
  inicio: string;
  fim: string | null;
  observacoes: string | null;
  vigente: boolean;
}

export interface Veiculo {
  id: string;
  placa: string;
  tipo: string;
  descricao: string | null;
  modelo: string | null;
  ano: number | null;
  situacao: string;
  observacoes: string | null;
  pode_sair: boolean;
}

export interface Ponto {
  id: string;
  os_id: string;
  os_numero: string | null;
  obra_codigo: string | null;
  obra_nome: string | null;
  codigo: string;
  tipo: string;
  descricao: string | null;
  latitude: string | null;
  longitude: string | null;
  cota: string | null;
  cota_referencia: string | null;
  profundidade_prevista: string | null;
  nivel_agua: string | null;
  situacao: string;
  motivo_situacao: string | null;
  observacoes: string | null;
  amostras: number;
  ensaios: number;
  profundidade_amostrada: string | null;
  /** 372 (SP-371a): a coordenada veio da locação pelo mapa — estimada. */
  coordenada_pelo_mapa?: boolean;
}

// ── equipes ──────────────────────────────────────────────────────────

export const verEquipes = (tipo?: string) =>
  api<Equipe[]>(`/equipes${tipo ? `?tipo=${tipo}` : ''}`);

export const verEquipe = (id: string) =>
  api<{ equipe: Equipe; membros: EquipeMembro[] }>(`/equipes/${id}`);

export const criarEquipe = (corpo: unknown) =>
  api<Equipe>('/equipes', { metodo: 'POST', corpo });

export const alterarEquipe = (id: string, corpo: unknown) =>
  api<Equipe>(`/equipes/${id}`, { metodo: 'PATCH', corpo });

export const incluirMembro = (id: string, corpo: unknown) =>
  api<{ equipe: Equipe; membros: EquipeMembro[] }>(
    `/equipes/${id}/membros`, { metodo: 'POST', corpo });

/** Encerrar, e não remover: o diário de março continua sendo de março. */
export const encerrarMembro = (id: string, mid: string, corpo: unknown) =>
  api<{ equipe: Equipe; membros: EquipeMembro[] }>(
    `/equipes/${id}/membros/${mid}/encerrar`, { metodo: 'POST', corpo });

// ── veículos ─────────────────────────────────────────────────────────

export const verVeiculos = () => api<Veiculo[]>('/veiculos');

export const criarVeiculo = (corpo: unknown) =>
  api<Veiculo>('/veiculos', { metodo: 'POST', corpo });

export const alterarVeiculo = (id: string, corpo: unknown) =>
  api<Veiculo>(`/veiculos/${id}`, { metodo: 'PATCH', corpo });

export const situacaoDoVeiculo = (id: string, corpo: unknown) =>
  api<Veiculo>(`/veiculos/${id}/situacao`, { metodo: 'POST', corpo });

// ── pontos de investigação ───────────────────────────────────────────

export const verPontosDaOs = (osId: string) =>
  api<Ponto[]>(`/os/${osId}/pontos`);

export const criarPonto = (osId: string, corpo: unknown) =>
  api<Ponto>(`/os/${osId}/pontos`, { metodo: 'POST', corpo });

export const alterarPonto = (id: string, corpo: unknown) =>
  api<Ponto>(`/pontos/${id}`, { metodo: 'PATCH', corpo });

export const situacaoDoPonto = (id: string, corpo: unknown) =>
  api<Ponto>(`/pontos/${id}/situacao`, { metodo: 'POST', corpo });
